// apps/web/src/methods/bodybuildingFitness.tsx
import * as React from 'react';
import type { MethodConfig, Exercise, ExercisePlan } from './MethodConfig';

const norm = (s?: string | null) => (s || '').trim().toLowerCase();

function sanitizeSetsInput(raw: string): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits === '') return '';
  let next = digits;
  if (next.length > 1) {
    next = next.replace(/^0+/, '');
    if (next === '') next = '0';
  }
  return next;
}

function parseSetsInput(value: string): number | null {
  if (value == null || value.trim() === '') return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  const intVal = Math.floor(num);
  return intVal >= 1 ? intVal : null;
}

export function isHoldExerciseLabel(label?: string | null): boolean {
  const value = norm(label);
  if (!value) return false;
  if (/\bhold\b/.test(value)) return true;
  // A bare plank is held, but its counted variants are not: "Plank Shoulder Tap"
  // is 15 taps, not 15 seconds. Must precede the plank rule below.
  if (/\bplank\b/.test(value)) {
    return !/\b(taps?|jacks?|walk-?outs?|up-?downs?|rows?|reach(es)?|drags?|to push-?ups?)\b/.test(value);
  }
  if (/\bl-?sit\b/.test(value)) return true;
  // Any hang is a timed hold (dead/active/bar/scap hang) — but NOT the barbell
  // hang lifts (hang clean/snatch), which are rep-based.
  if (/\bhang\b/.test(value) && !/\b(clean|snatch)\b/.test(value)) return true;
  if (/\bring support\b/.test(value)) return true;
  return false;
}

function isShuttleSprintExerciseLabel(label?: string | null): boolean {
  const value = norm(label);
  if (!value) return false;
  return /\bshuttle sprints?\b/.test(value);
}

function isDistanceExerciseLabel(label?: string | null): boolean {
  const value = norm(label);
  if (!value) return false;
  if (/\bbear crawl\b/.test(value)) return true;
  if (isShuttleSprintExerciseLabel(value)) return true;
  return /\bmeter(s)?\b/.test(value);
}

function isWeightlessDistanceExerciseLabel(label?: string | null): boolean {
  const value = norm(label);
  if (!value) return false;
  if (/\bbear crawl\b/.test(value)) return false;
  if (isShuttleSprintExerciseLabel(value)) return false;
  return isDistanceExerciseLabel(label);
}

function formatDurationSeconds(value?: number | null): string {
  if (value == null || !Number.isFinite(value) || value < 0) return '';
  const totalSeconds = Math.floor(value);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${minutes}:${pad(seconds)}`;
}

function parseDurationToSeconds(raw: string): number | null {
  const value = String(raw ?? '').trim().toLowerCase();
  if (!value) return null;

  const hmsMatch = value.match(/^(\d+)\s*:\s*(\d{1,2})(?:\s*:\s*(\d{1,2}))?$/);
  if (hmsMatch) {
    if (hmsMatch[3] != null) {
      const h = Number(hmsMatch[1]);
      const m = Number(hmsMatch[2]);
      const s = Number(hmsMatch[3]);
      if (!Number.isFinite(h) || !Number.isFinite(m) || !Number.isFinite(s)) return null;
      if (m > 59 || s > 59) return null;
      return h * 3600 + m * 60 + s;
    }
    const m = Number(hmsMatch[1]);
    const s = Number(hmsMatch[2]);
    if (!Number.isFinite(m) || !Number.isFinite(s)) return null;
    if (s > 59) return null;
    return m * 60 + s;
  }

  const minutesSecondsMatch = value.match(
    /^(\d+)\s*m(?:in(?:ute)?s?)?(?:\s+(\d+)\s*s(?:ec(?:ond)?s?)?)?$/
  );
  if (minutesSecondsMatch) {
    const m = Number(minutesSecondsMatch[1]);
    const s = minutesSecondsMatch[2] != null ? Number(minutesSecondsMatch[2]) : 0;
    if (!Number.isFinite(m) || !Number.isFinite(s)) return null;
    if (s > 59) return null;
    return m * 60 + s;
  }

  const secondsOnlyMatch = value.match(/^(\d+)\s*s(?:ec(?:ond)?s?)?$/);
  if (secondsOnlyMatch) {
    const s = Number(secondsOnlyMatch[1]);
    return Number.isFinite(s) ? s : null;
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  return Math.floor(numeric);
}

export function isBodybuildingFitnessMethod(
  m?: { raw?: string | null; uniqueKey?: string | null; type?: string | null }
) {
  const uk = norm(m?.uniqueKey);
  const raw = norm(m?.raw);
  return uk === 'catalog:bodybuilding/fitness' || raw === 'bodybuilding/fitness';
}

export const bodybuildingFitnessConfig: MethodConfig = {
  isMatch: isBodybuildingFitnessMethod,

  Editor({ exercises, selectedExerciseIds, plans, setPlans, onToggleExercise, amrap = false }) {
    const exerciseById = React.useMemo(() => {
      const next = new Map<string, Exercise>();
      for (const ex of exercises) {
        if (ex?.id) next.set(ex.id, ex);
      }
      return next;
    }, [exercises]);

    const isHoldExerciseId = React.useCallback(
      (id: string) => {
        const ex = exerciseById.get(id);
        const label = ex?.nameRaw ?? ex?.raw ?? id;
        return isHoldExerciseLabel(label);
      },
      [exerciseById]
    );
    const isDistanceExerciseId = React.useCallback(
      (id: string) => {
        const ex = exerciseById.get(id);
        const label = ex?.nameRaw ?? ex?.raw ?? id;
        return isDistanceExerciseLabel(label);
      },
      [exerciseById]
    );
    const isShuttleSprintExerciseId = React.useCallback(
      (id: string) => {
        const ex = exerciseById.get(id);
        const label = ex?.nameRaw ?? ex?.raw ?? id;
        return isShuttleSprintExerciseLabel(label);
      },
      [exerciseById]
    );
    const isWeightlessDistanceExerciseId = React.useCallback(
      (id: string) => {
        const ex = exerciseById.get(id);
        const label = ex?.nameRaw ?? ex?.raw ?? id;
        return isWeightlessDistanceExerciseLabel(label);
      },
      [exerciseById]
    );

    const defaultPlan = (isHold = false): ExercisePlan => ({
      sets: 3,
      reps: isHold ? [30, 30, 30] : [12, 10, 8],
      weights: [],
      dropset: false,
      dropsetReps: null,
      dropsetWeight: null,
      supersetWith: null,
    });

    // default plan: classic reps, blank weights
    const ensurePlan = (id: string): ExercisePlan =>
      plans[id] || defaultPlan(isHoldExerciseId(id));

    const [setsDrafts, setSetsDrafts] = React.useState<Record<string, string>>({});
    const [timeDrafts, setTimeDrafts] = React.useState<Record<string, string>>({});
    // Raw text for weight inputs so a decimal comma (e.g. 17,5) can be typed.
    const [weightDrafts, setWeightDrafts] = React.useState<Record<string, string>>({});

    React.useEffect(() => {
      setSetsDrafts((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const id of selectedExerciseIds) {
          if (next[id] == null) {
            const plan = ensurePlan(id);
            const draftValue = plan.setsDraft != null ? String(plan.setsDraft) : String(plan.sets ?? 1);
            next[id] = draftValue;
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, [plans, selectedExerciseIds, isHoldExerciseId]);

    React.useEffect(() => {
      setSetsDrafts((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const id of selectedExerciseIds) {
          const plan = plans[id];
          if (!plan || plan.setsDraft == null) continue;
          const desired = String(plan.setsDraft);
          if (next[id] !== desired) {
            next[id] = desired;
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, [plans, selectedExerciseIds]);

    React.useEffect(() => {
      setTimeDrafts((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const id of selectedExerciseIds) {
          if (!isHoldExerciseId(id)) continue;
          const plan = ensurePlan(id);
          const reps = Array.isArray(plan.reps) ? plan.reps : [];
          for (let i = 0; i < reps.length; i += 1) {
            const key = `${id}:${i}`;
            if (next[key] != null) continue;
            next[key] = formatDurationSeconds(reps[i] ?? null);
            changed = true;
          }
          if (plan.dropset) {
            const dropsetKey = `${id}:dropset`;
            if (next[dropsetKey] == null) {
              next[dropsetKey] = formatDurationSeconds(plan.dropsetReps ?? null);
              changed = true;
            }
          }
        }
        return changed ? next : prev;
      });
    }, [ensurePlan, isHoldExerciseId, selectedExerciseIds]);

    const toggleSupersetBetween = (firstId: string, secondId: string, enabled: boolean) =>
      setPlans((prev) => {
        const next = { ...prev };
        const clearIds = new Set([firstId, secondId]);
        for (const [planId, plan] of Object.entries(next)) {
          if (!plan) continue;
          const supersetWith = plan.supersetWith ?? null;
          if (clearIds.has(planId) || (supersetWith && clearIds.has(supersetWith))) {
            next[planId] = { ...plan, supersetWith: null };
          }
        }
        if (enabled) {
          const firstPlan = next[firstId] || defaultPlan(isHoldExerciseId(firstId));
          next[firstId] = { ...firstPlan, supersetWith: secondId };
        }
        return next;
      });

    const renderSupersetToggle = (
      firstId: string,
      secondId: string,
      checked: boolean,
      inGroup: boolean,
      keyOverride?: string
    ) => (
      <div
        key={keyOverride ?? `superset-${firstId}-${secondId}`}
        className={inGroup ? 'flex items-center justify-center py-1' : 'flex items-center justify-center'}
      >
        <label className="inline-flex items-center gap-2 text-xs uppercase tracking-wide opacity-70">
          <input
            type="checkbox"
            className="form-checkbox h-4 w-4"
            checked={checked}
            onChange={(e) => toggleSupersetBetween(firstId, secondId, e.target.checked)}
          />
          Superset
        </label>
      </div>
    );

    const renderExerciseCard = (
      id: string,
      options: { inSupersetGroup?: boolean; isSupersetSecond?: boolean },
      keyOverride?: string
    ) => {
      const ex = exercises.find((e: Exercise) => e.id === id);
      const lbl = ex?.nameRaw ?? ex?.raw ?? id;
      const holdExercise = isHoldExerciseId(id);
      const shuttleSprintExercise = isShuttleSprintExerciseId(id);
      const distanceExercise = isDistanceExerciseId(id) && !shuttleSprintExercise;
      const weightlessDistanceExercise = isWeightlessDistanceExerciseId(id);
      const gridCols = weightlessDistanceExercise ? 'grid-cols-[40px,1fr]' : 'grid-cols-[40px,1fr,1fr]';
      const primaryFieldLabel = holdExercise ? 'Time (mm:ss)' : distanceExercise ? 'Distance (m)' : 'Reps';
      const primaryFieldPlaceholder = distanceExercise ? 'Meters' : 'Reps';
      const primaryFieldAriaLabel = distanceExercise ? 'Distance' : 'Reps';
      const secondaryFieldLabel = shuttleSprintExercise ? 'Distance (m)' : 'Weight (kg)';
      const plan = ensurePlan(id);
      const inSupersetGroup = !!options.inSupersetGroup;
      const isSupersetSecond = !!options.isSupersetSecond;
      const setsInput = setsDrafts[id] ?? String(plan.sets ?? 1);
      const setsInvalid = parseSetsInput(setsInput) == null;

      const handleSetsChange = (raw: string) => {
        const nextValue = sanitizeSetsInput(raw);
        setSetsDrafts((prev) => ({ ...prev, [id]: nextValue }));
        const parsed = parseSetsInput(nextValue);
        setPlans((prev) => {
          const cur = prev[id] || defaultPlan();
          let nextPlan: ExercisePlan = { ...cur, setsDraft: nextValue };
          if (parsed != null) {
            const n = Math.max(1, Math.floor(parsed));
            const reps = Array.isArray(cur.reps) ? [...cur.reps] : [];
            const weights = Array.isArray(cur.weights) ? [...cur.weights] : [];
            const defaultRep = holdExercise ? 30 : 10;
            // Uniform means the sets are meant to be identical, so new ones grow
            // from the shared pair instead of the generic default and a blank.
            const uniform = !!cur.uniform;
            const sharedRep = uniform ? reps[0] ?? defaultRep : defaultRep;
            const sharedWeight = uniform ? weights[0] ?? null : null;

            if (n > reps.length) {
              while (reps.length < n) reps.push(sharedRep);
              while (weights.length < n) weights.push(sharedWeight); // blank unless uniform
            } else {
              reps.length = n;
              weights.length = Math.min(weights.length, n);
            }

            nextPlan = { ...nextPlan, sets: n, reps, weights };
          }
          return { ...prev, [id]: nextPlan };
        });
      };

      /**
       * Uniform collapses the sets into one row, so an edit to that row has to
       * land on every set — otherwise only set #1 would carry the value and the
       * rest would keep whatever they had before the box was unticked.
       */
      const writeAcross = (
        arr: (number | null)[],
        index: number,
        value: number | null,
        uniform: boolean,
        count: number
      ): (number | null)[] => {
        if (!uniform) {
          const next = [...arr];
          next[index] = value;
          return next;
        }
        return Array.from({ length: Math.max(1, count) }, () => value);
      };

      // REPS: allow blanks while typing
      const setRepAt = (i: number, raw: string) =>
        setPlans((prev) => {
          const cur = ensurePlan(id);
          let value: number | null;
          if (raw === '' || raw == null) {
            value = null;                               // blank
          } else {
            const v = Number(raw);
            value = Number.isFinite(v) && v >= 1 ? Math.floor(v) : 1;
          }
          const reps = writeAcross(cur.reps as (number | null)[], i, value, !!cur.uniform, cur.sets);
          return { ...prev, [id]: { ...cur, reps } };
        });

      const setTimeAt = (i: number, raw: string) => {
        const key = `${id}:${i}`;
        setTimeDrafts((prev) => ({ ...prev, [key]: raw }));
        if (raw.trim() === '') {
          setPlans((prev) => {
            const cur = ensurePlan(id);
            const reps = writeAcross(cur.reps as (number | null)[], i, null, !!cur.uniform, cur.sets);
            return { ...prev, [id]: { ...cur, reps } };
          });
          return;
        }
        const sec = parseDurationToSeconds(raw);
        if (sec == null) return;
        setPlans((prev) => {
          const cur = ensurePlan(id);
          const reps = writeAcross(cur.reps as (number | null)[], i, sec, !!cur.uniform, cur.sets);
          return { ...prev, [id]: { ...cur, reps } };
        });
      };

      // Accept '' as blank (null), otherwise coerce to >= 0 number
      const setWeightAt = (i: number, raw: string, asDistance = false) =>
        setPlans((prev) => {
          const cur = ensurePlan(id);
          let value: number | null;
          if (raw === '' || raw == null) {
            value = null;
          } else {
            const v = Number(String(raw).replace(',', '.'));
            if (asDistance) {
              value = Number.isFinite(v) && v >= 1 ? Math.floor(v) : 1;
            } else {
              value = Number.isFinite(v) && v >= 0 ? v : 0;
            }
          }
          const weights = writeAcross(
            (cur.weights || []) as (number | null)[],
            i,
            value,
            !!cur.uniform,
            cur.sets
          );
          return { ...prev, [id]: { ...cur, weights } };
        });

      /**
       * Unticking "Individual" folds the sets down to one row. Set #1 supplies
       * the shared pair, so the collapsed row opens on a value the user already
       * chose rather than a blank. Re-ticking leaves those values in place and
       * just shows the rows again, so it is a safe thing to toggle.
       */
      const toggleIndividual = (individual: boolean) =>
        setPlans((prev) => {
          const cur = prev[id] || defaultPlan(isHoldExerciseId(id));
          if (individual) return { ...prev, [id]: { ...cur, uniform: false } };
          const count = Math.max(1, Math.floor(Number(cur.sets) || 1));
          const rep = Array.isArray(cur.reps) ? cur.reps[0] ?? null : null;
          const weight = Array.isArray(cur.weights) ? cur.weights[0] ?? null : null;
          return {
            ...prev,
            [id]: {
              ...cur,
              uniform: true,
              reps: Array.from({ length: count }, () => rep),
              weights: Array.from({ length: count }, () => weight),
            },
          };
        });

      const toggleDropset = (enabled: boolean) =>
        setPlans((prev) => {
          const cur = prev[id] || defaultPlan();
          if (!enabled) {
            return { ...prev, [id]: { ...cur, dropset: false } };
          }
          return {
            ...prev,
            [id]: {
              ...cur,
              dropset: true,
              dropsetReps: cur.dropsetReps ?? null,
              dropsetWeight: cur.dropsetWeight ?? null,
            },
          };
        });

      const setDropsetRep = (raw: string) =>
        setPlans((prev) => {
          const cur = prev[id] || defaultPlan();
          let dropsetReps: number | null = null;
          if (raw !== '' && raw != null) {
            const v = Number(raw);
            dropsetReps = Number.isFinite(v) && v >= 1 ? Math.floor(v) : 1;
          }
          return { ...prev, [id]: { ...cur, dropsetReps, dropset: true } };
        });

      const setDropsetTime = (raw: string) => {
        const key = `${id}:dropset`;
        setTimeDrafts((prev) => ({ ...prev, [key]: raw }));
        if (raw.trim() === '') {
          setPlans((prev) => {
            const cur = prev[id] || defaultPlan(true);
            return { ...prev, [id]: { ...cur, dropsetReps: null, dropset: true } };
          });
          return;
        }
        const sec = parseDurationToSeconds(raw);
        if (sec == null) return;
        setPlans((prev) => {
          const cur = prev[id] || defaultPlan(true);
          return { ...prev, [id]: { ...cur, dropsetReps: sec, dropset: true } };
        });
      };

      const setDropsetWeight = (raw: string, asDistance = false) =>
        setPlans((prev) => {
          const cur = prev[id] || defaultPlan();
          let dropsetWeight: number | null = null;
          if (raw !== '' && raw != null) {
            const v = Number(String(raw).replace(',', '.'));
            if (asDistance) {
              dropsetWeight = Number.isFinite(v) && v >= 1 ? Math.floor(v) : 1;
            } else {
              dropsetWeight = Number.isFinite(v) && v >= 0 ? v : 0;
            }
          }
          return { ...prev, [id]: { ...cur, dropsetWeight, dropset: true } };
        });

      const dropsetEnabled = !!plan.dropset;
      const uniformEnabled = !!plan.uniform;
      const cardClassName = inSupersetGroup
        ? 'rounded-md border border-transparent p-3'
        : 'rounded-lg border p-3';

      return (
        <div key={keyOverride ?? id} className={cardClassName}>
          <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
            <div className="font-medium min-w-0 break-words">{lbl}</div>
            <button
              type="button"
              onClick={() => onToggleExercise(id)}
              className="text-xs opacity-70 hover:opacity-100 underline shrink-0"
            >
              remove
            </button>
          </div>

          <div className="flex items-center gap-3 mb-2">
            <label className="text-sm">Sets</label>
            {amrap ? (
              // AMRAP: as many rounds as possible, so the set count isn't known — show a read-only chip.
              <span
                className="w-20 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/60 px-2 py-1 text-center text-xs font-semibold uppercase tracking-wide opacity-70 select-none cursor-not-allowed"
                title="AMRAP — as many rounds as possible; the number of sets isn't fixed"
              >
                AMRAP
              </span>
            ) : (
              <input
                type="text"
                inputMode="numeric"
                className={[
                  'input w-20',
                  setsInvalid ? 'border-rose-500 focus:border-rose-500 focus:ring-rose-200' : '',
                ].join(' ')}
                value={setsInput}
                onChange={(e) => handleSetsChange(e.target.value)}
                aria-invalid={setsInvalid}
              />
            )}
            {!amrap && (
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4"
                  checked={dropsetEnabled}
                  onChange={(e) => toggleDropset(e.target.checked)}
                />
                Dropset
              </label>
            )}
            {!amrap && (
              <label
                className="inline-flex items-center gap-2 text-sm"
                title="One weight and one rep count for every set. Untick to vary them set by set."
              >
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4"
                  checked={!uniformEnabled}
                  onChange={(e) => toggleIndividual(e.target.checked)}
                />
                Individual
              </label>
            )}
          </div>

          <div className="max-h-64 overflow-auto pr-1">
            {/* Column headers */}
            <div className={`grid ${gridCols} gap-2 text-xs uppercase tracking-wide opacity-60 border-b pb-1 sticky top-0 z-10 bg-white/95 dark:bg-gray-950/95 backdrop-blur`}>
              <div>Set</div>
              <div>{primaryFieldLabel}</div>
              {!weightlessDistanceExercise && <div>{secondaryFieldLabel}</div>}
            </div>

            {/* Rows */}
            <div className="grid gap-2 mt-2 pb-1">
              {Array.from({ length: amrap || uniformEnabled ? 1 : plan.sets }).map((_, i) => {
                const setNumber = isSupersetSecond ? i + 1.5 : i + 1;
                // Uniform shows a single row standing in for the whole block, so
                // it is labelled by its span rather than by a set number.
                const setLabel = uniformEnabled
                  ? plan.sets > 1
                    ? `#1–${plan.sets}`
                    : '#1'
                  : isSupersetSecond
                    ? `#${setNumber} Super`
                    : `#${setNumber}`;
                return (
                  <div key={i} className={`grid ${gridCols} gap-2 items-center`}>
                    <div className="text-xs opacity-70">{setLabel}</div>

                    {holdExercise ? (
                      <input
                        type="text"
                        inputMode="text"
                        className={[
                          'input',
                          (() => {
                            const draftKey = `${id}:${i}`;
                            const raw = timeDrafts[draftKey] ?? formatDurationSeconds(plan.reps[i] ?? null);
                            return raw.trim() !== '' && parseDurationToSeconds(raw) == null
                              ? 'border-rose-500 focus:border-rose-500 focus:ring-rose-200'
                              : '';
                          })(),
                        ].join(' ')}
                        value={timeDrafts[`${id}:${i}`] ?? formatDurationSeconds(plan.reps[i] ?? null)}
                        onChange={(e) => setTimeAt(i, e.target.value)}
                        placeholder="mm:ss"
                        aria-label={`Time set ${i + 1}`}
                      />
                    ) : (
                      <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        className="input"
                        value={plan.reps[i] ?? ''}
                        onChange={(e) => setRepAt(i, e.target.value)}
                        placeholder={primaryFieldPlaceholder}
                        aria-label={`${primaryFieldAriaLabel} set ${i + 1}`}
                      />
                    )}

                    {!weightlessDistanceExercise && (
                      <input
                        type="text"
                        inputMode={shuttleSprintExercise ? 'numeric' : 'decimal'}
                        className="input"
                        value={weightDrafts[`${id}:${i}`] ?? (plan.weights[i] || '')}  // show blank, not 0
                        onChange={(e) => { const raw = e.target.value; setWeightDrafts((d) => ({ ...d, [`${id}:${i}`]: raw })); setWeightAt(i, raw, shuttleSprintExercise); }} // store null if ''
                        placeholder={shuttleSprintExercise ? 'Meters' : 'kg'}
                        aria-label={`${shuttleSprintExercise ? 'Distance' : 'Weight'} set ${i + 1}`}
                      />
                    )}
                  </div>
                );
              })}
              {!amrap && dropsetEnabled && (
                <div className={`grid ${gridCols} gap-3 items-center`}>
                  <div className="text-xs opacity-70">#{plan.sets + 0.5} DS</div>
                  {holdExercise ? (
                    <input
                      type="text"
                      inputMode="text"
                      className={[
                        'input',
                        (() => {
                          const raw =
                            timeDrafts[`${id}:dropset`] ??
                            formatDurationSeconds(plan.dropsetReps ?? null);
                          return raw.trim() !== '' && parseDurationToSeconds(raw) == null
                            ? 'border-rose-500 focus:border-rose-500 focus:ring-rose-200'
                            : '';
                        })(),
                      ].join(' ')}
                      value={timeDrafts[`${id}:dropset`] ?? formatDurationSeconds(plan.dropsetReps ?? null)}
                      onChange={(e) => setDropsetTime(e.target.value)}
                      placeholder="mm:ss"
                      aria-label={`Dropset time for ${lbl}`}
                    />
                  ) : (
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      className="input"
                      value={plan.dropsetReps ?? ''}
                      onChange={(e) => setDropsetRep(e.target.value)}
                      placeholder={primaryFieldPlaceholder}
                      aria-label={`Dropset ${distanceExercise ? 'distance' : 'reps'} for ${lbl}`}
                    />
                  )}
                  {!weightlessDistanceExercise && (
                    <input
                      type="text"
                      inputMode={shuttleSprintExercise ? 'numeric' : 'decimal'}
                      className="input"
                      value={weightDrafts[`${id}:dropset`] ?? (plan.dropsetWeight || '')}
                      onChange={(e) => { const raw = e.target.value; setWeightDrafts((d) => ({ ...d, [`${id}:dropset`]: raw })); setDropsetWeight(raw, shuttleSprintExercise); }}
                      placeholder={shuttleSprintExercise ? 'Meters' : 'kg'}
                      aria-label={`Dropset ${shuttleSprintExercise ? 'distance' : 'weight'} for ${lbl}`}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    const rows: React.ReactNode[] = [];
    for (let i = 0; i < selectedExerciseIds.length; i++) {
      const id = selectedExerciseIds[i];
      const nextId = selectedExerciseIds[i + 1];
      const hasSupersetWithNext =
        typeof nextId === 'string' && plans[id]?.supersetWith === nextId;

      if (hasSupersetWithNext && nextId) {
        rows.push(
          <div
            key={`pair-${id}-${nextId}`}
            className="rounded-lg border-2 border-blue-500/60 dark:border-blue-400/60 p-2 space-y-2"
          >
            {renderExerciseCard(id, { inSupersetGroup: true })}
            {renderSupersetToggle(id, nextId, true, true)}
            {renderExerciseCard(nextId, { inSupersetGroup: true, isSupersetSecond: true })}
          </div>
        );
        if (i + 2 < selectedExerciseIds.length) {
          const afterNextId = selectedExerciseIds[i + 2];
          rows.push(renderSupersetToggle(nextId, afterNextId, false, false, `superset-${nextId}-${afterNextId}`));
        }
        i += 1;
        continue;
      }

      const prevId = selectedExerciseIds[i - 1];
      const isSupersetSecond =
        typeof prevId === 'string' && plans[prevId]?.supersetWith === id;
      rows.push(renderExerciseCard(id, { isSupersetSecond }, id));
      if (nextId) {
        rows.push(renderSupersetToggle(id, nextId, false, false, `superset-${id}-${nextId}`));
      }
    }

    return (
      <div className="mt-2 space-y-3">{rows}</div>
    );
  },

  buildPayload({ selectedExerciseIds, plans }) {
    return {
      exercisePlans: selectedExerciseIds.map((id, idx) => {
        const p = plans[id] || {
          sets: 3,
          reps: [12, 10, 8],
          weights: [] as (number | null)[],
          dropset: false,
          dropsetReps: null,
          dropsetWeight: null,
        };

        const baseSets = Math.max(1, Math.floor(Number(p.sets) || 1));
        const reps = Array.isArray(p.reps) ? [...p.reps] : [];
        const weightsRaw = Array.isArray(p.weights) ? [...p.weights] : [];

        while (reps.length < baseSets) reps.push(10);
        if (reps.length > baseSets) reps.length = baseSets;

        while (weightsRaw.length < baseSets) weightsRaw.push(null);
        if (weightsRaw.length > baseSets) weightsRaw.length = baseSets;

        const dropsetEnabled = !!p.dropset;
        if (dropsetEnabled) {
          reps.push(p.dropsetReps ?? null);
          weightsRaw.push(p.dropsetWeight ?? null);
        }

        const totalSets = baseSets + (dropsetEnabled ? 1 : 0);

        // Only send weights if there's at least one real number.
        const hasAnyNumber = weightsRaw.some((w) => w != null && w !== 0);
        const weights = hasAnyNumber
          ? weightsRaw.map((w) => (w == null ? 0 : w)) // API expects number[]
          : [];

        return {
          exerciseId: id,
          order: idx,
          sets: totalSets,
          reps,
          weights,
        };
      }),
    };
  },
};
